'use client';

import classNames from 'classnames';
import { FC, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { PropsWithClassname } from '@/app/types';

import { Donation } from '@/app/api';
import Checkbox from '@/app/components/Checkbox/Checkbox';
import { formatPriceInCents } from '@/app/utils/formatting';
import { format } from 'date-fns';
import Button, { ButtonHTMLTag, ButtonSize, ButtonStyle } from '@/app/components/Button/Button';
import { useParams } from 'next/navigation';
import { Routes } from '@/app/enums';
import Big from 'big.js';

interface DonateAvailableFundsWidgetProps extends PropsWithClassname {
  availableFunds: Donation[];
}

const getDonationsSum = (acc: Big.Big, donation: Donation) => acc.plus(donation.amountInCents);

const DonateAvailableFundsWidget: FC<DonateAvailableFundsWidgetProps> = ({ availableFunds, className }) => {
  const { t } = useTranslation();

  const allAvailableFundsSum = useMemo(
    () => availableFunds.reduce(getDonationsSum, new Big(0)).toNumber(),
    [availableFunds],
  );

  const [usedIds, setUsedIds] = useState(availableFunds.map((donation) => donation.publicId));
  const params = useParams();

  const { sumToDonate, link } = useMemo(() => {
    const sumToDonate = availableFunds
      .filter((fund) => usedIds.includes(fund.publicId))
      .reduce(getDonationsSum, new Big(0))
      .toNumber();

    const idsParam = usedIds.map((id) => `donations%5B%5D=${id}`).join('&');

    return {
      sumToDonate,
      link:
        sumToDonate > 0
          ? `${process.env.NEXT_PUBLIC_API_BASE_URL}/${Routes.ASSIGN_UNUSED_GOOD_HABIT}/${params?.numericId}?${idsParam}`
          : '#',
    };
  }, [availableFunds, params?.numericId, usedIds, usedIds.length]);

  return (
    <div
      className={classNames(className, 'py-[24px] px-[16px] relative rounded-sm shadow-md border border-neutral-light')}
    >
      <h2 className="font-medium text-[20px] leading-[24px]">
        {t('components.DonationForm.supportStoryWithyOurDonation', { count: availableFunds.length })}
      </h2>

      <p className="text-[16px] mt-[8px] mb-[24px]">
        <Trans
          i18nKey={
            availableFunds.length > 1
              ? 'components.DonationForm.multipleDonateAvailableFundsDescription'
              : 'components.DonationForm.singleDonateAvailableFundsDescription'
          }
          values={{
            price: formatPriceInCents(allAvailableFundsSum),
            count: availableFunds.length,
            from_gifts: t('components.DonationForm.from_gifts', { count: availableFunds.length }),
          }}
          components={{ strong: <span className="font-medium" /> }}
        />
      </p>

      {availableFunds.length > 1 &&
        availableFunds.map((donation) => (
          <Checkbox
            className="mt-[8px]"
            onChange={(nextIsChecked) =>
              nextIsChecked
                ? setUsedIds([...usedIds, donation.publicId])
                : setUsedIds(usedIds.filter((id) => id !== donation.publicId))
            }
            key={donation.publicId}
            isChecked={usedIds.includes(donation.publicId)}
            label={
              <div>
                <span className="font-medium">{formatPriceInCents(donation.amountInCents)}</span>{' '}
                <span>({format(new Date(donation.createdAt), 'd.M.')})</span>
              </div>
            }
          />
        ))}

      <Button
        tagSpecificProps={{ htmlTag: ButtonHTMLTag.LINK, href: link }}
        style={ButtonStyle.PRIMARY}
        size={ButtonSize.MEDIUM}
        className="w-full text-center py-[16px] mt-[8px] block"
      >
        {t('components.DonationForm.donateAvailableFundsSubmit', { amount: formatPriceInCents(sumToDonate) })}
      </Button>
    </div>
  );
};

export default DonateAvailableFundsWidget;
